import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ConfirmationService } from 'primeng/api';
import { LoggerService } from '../logger-service/logger.service';
import { AuthService } from '../auth-service/auth-service.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {

  private inactivityTotalTime: number = environment.inactivityTime; // Total inactivity time (e.g., 10 minutes in milliseconds)
  private inactivityWarningTime: number = environment.inactivityWarningTime; // Warning time before expiration (e.g., 5 minutes in milliseconds)
  private inactivityTotalTimeoutId: any; // Timer for the expiration dialog
  private inactivityWarningTimeoutId: any; // Timer for the warning dialog
  private progressBarTimeoutId: any; // Timer for the progressbar
  private inactivitySubject = new Subject<boolean>();
  private sessionExpired: boolean = false; // Flag to track if the session has expired
  private openedAnySessionDialog: boolean = false; //Flag to track if is opened any dialog, e.g. your session will exprity soon, do you wish to logout, your session expired...
  private lastWarningTime: number = Date.now();
  private progressValue: number = 0; // Current value for progressbar

  constructor(
    private ngZone: NgZone,
    private confirmationService: ConfirmationService,
    private loggerService: LoggerService,
    private authService: AuthService
  ) { }

  setSessionExpiredAndNotify(): void {
    this.sessionExpired = true;
    this.clearWarningAndTotalTimers();
    this.clearProgressBarTimer();
    this.authService.sessionExpired(); //notify session expiry (will be performed redirecting to sessionexpire page, clearing sessionstorage)
  }

  // Method to start monitoring inactivity
  public startMonitoring(): void {
    this.resetWarningAndTotalTimeouts();
    this.addEventListeners();

    this.loggerService.info('Starting Monitoring user inactivity');
    this.loggerService.info(`InactivityTotalTime: ${this.timeToString(this.inactivityTotalTime)}`);
    this.loggerService.info(`InactivityWarningTime: ${this.timeToString(this.inactivityWarningTime)}`);  
  }

  public timeToString(timeMiliseconds: number): string {
    if (timeMiliseconds >= 60000) {
      return (timeMiliseconds / 60000) + ' minute(s)';
    }
    else if (timeMiliseconds < 60000) {
      return (timeMiliseconds / 1000) + ' second(s)';
    }
    else {
      return "undefined"
    }
  }

  // Add listeners to detect user activity
  private addEventListeners(): void {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => {
      window.addEventListener(event, this.handleUserActivity.bind(this));
    });
  }

  // Handle user activity
  private handleUserActivity(): void {
    if (!this.openedAnySessionDialog) {
      this.resetWarningAndTotalTimeouts(); // Reset timers only if the session has not expired and the warning dialog is not active
    }
  }

  private clearWarningAndTotalTimers(): void {
    // Clear existing timers
    if (this.inactivityWarningTimeoutId) {
      clearTimeout(this.inactivityWarningTimeoutId);
    }
    if (this.inactivityTotalTimeoutId) {
      clearTimeout(this.inactivityTotalTimeoutId);
    }
  }

  private clearProgressBarTimer(): void {
    if (this.progressBarTimeoutId) {
      clearInterval(this.progressBarTimeoutId);
      this.progressBarTimeoutId = null; // Reset the variable
      this.progressValue = 0;
    }
  }

  // Reset the inactivity timers
  private resetWarningAndTotalTimeouts(): void {
    this.clearWarningAndTotalTimers();
    // Set the warning timer
    this.ngZone.runOutsideAngular(() => {
      this.inactivityWarningTimeoutId = setTimeout(() => {
        this.ngZone.run(() => {
          this.showSessionInactivityWarningDialog(Date.now());
        });
      }, this.inactivityWarningTime);

      // Set the expiration timer
      this.inactivityTotalTimeoutId = setTimeout(() => {
        this.ngZone.run(() => {
          this.showSessionExpiredDialog();
        });
      }, this.inactivityTotalTime);
    });
  }

  //Calculate the time when the session expires
  private calculateSessionExpirationTime(): string {
    const remainingTime = environment.inactivityTime - environment.inactivityWarningTime;
    const expirationTime = this.lastWarningTime + remainingTime;
    const expirationDate = new Date(expirationTime);
    const formattedExpirationTime = expirationDate.toLocaleTimeString();

    return formattedExpirationTime;
  }

  //Calculate the current value for the progressbar
  private calculateProgressbarValue(): number {
    const remainingTime = environment.inactivityTime - environment.inactivityWarningTime; // Total time for the progress bar
    const expirationTime = this.lastWarningTime + remainingTime; // Calculate the expiration time

    const currentTime = Date.now(); // Get the current time
    const elapsedTime = currentTime - this.lastWarningTime; // Time elapsed since the warning was shown
    const totalTime = expirationTime - this.lastWarningTime; // Total time between warning and expiration
    const newProgressbarValue = elapsedTime / totalTime;

    return newProgressbarValue;
  }

  //start the timer for the progressbar
  startCountDownProgressbar() {
    if (!this.progressBarTimeoutId) {
      this.progressValue = 0;
      this.progressBarTimeoutId = setInterval(() => {
        // Calculate progress as a percentage and round it to the nearest integer
        this.progressValue = Math.min(Math.round((this.calculateProgressbarValue()) * 100), 100); // Ensure it doesn't exceed 100%
        // Stop the interval when progress reaches 100%
        if (this.progressValue >= 100) {
          this.clearProgressBarTimer();
        }
      }, 500);
    }
  }

  // Show the warning dialog
  private showSessionInactivityWarningDialog(warningTime?: number): void {
    if (this.sessionExpired) {
      return;
    }
    this.confirmationService.close(); //close previous dialog if is displayed any
    this.openedAnySessionDialog = true;
    this.lastWarningTime = warningTime ? warningTime : this.lastWarningTime;

    this.confirmationService.confirm({
      key: 'sessionInactivityWarningDialog',
      header: 'Your session is about to expire',
      message: `If you don't extend your session it will expire at ${this.calculateSessionExpirationTime()}. All current unsaved data will be lost.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Extend Session',
      rejectLabel: 'Logout',
      accept: () => {
        this.openedAnySessionDialog = false;
        this.extendSession();
      },
      reject: () => {
        this.openedAnySessionDialog = false;
        this.showLogoutConfirmationDialog();
      }
    });

    this.startCountDownProgressbar();
  }

  // Show the logout confirmation dialog
  private showLogoutConfirmationDialog(): void {
    if (this.sessionExpired) {
      return;
    }
    this.confirmationService.close(); //close previous dialog if is displayed any
    this.openedAnySessionDialog = true;
    
    this.confirmationService.confirm({
      key: 'sessionLogoutDialog',
      header: 'Please confirm you wish to logout',
      message: 'All current unsaved data will be lost.',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Yes, Logout',
      rejectLabel: 'Cancel',
      accept: () => {
        this.openedAnySessionDialog = false;
        this.logout();
      },
      reject: () => {
        this.openedAnySessionDialog = false;
        this.showSessionInactivityWarningDialog();
      }
    });
  }

  // Show the session expiration page
  private showSessionExpiredDialog(): void {
    this.openedAnySessionDialog = true;
    this.confirmationService.close(); //close previous dialog if is displayed any
    this.setSessionExpiredAndNotify(); //main function to execute all steps when session expires
  }

  isSessionExpired(): boolean {
    return this.sessionExpired;
  }

  //Extend the session
  private extendSession(): void {
    this.sessionExpired = false; // Reset the session expired flag
    this.resetWarningAndTotalTimeouts(); // Reset the timers to start monitoring again
    this.clearProgressBarTimer(); //stop the progressbar timer
  }

  //Logout the user
  private logout(): void {
    this.inactivitySubject.next(true); //returing the signal to inactivity observable in organisation-selector.components.ts
  }

  // Method to subscribe to inactivity status
  public getInactivityObservable() {
    return this.inactivitySubject.asObservable();
  }
}
